import { type ReactNode, useRef } from "react";
import { configureApiClient } from "../api/client";
import { useAuth } from "./AuthProvider";

interface ApiClientProviderProps {
	apiUrl?: string;
	apiGraphqlUrl?: string;
	children: ReactNode;
}

export function ApiClientProvider({
	apiUrl,
	apiGraphqlUrl,
	children,
}: ApiClientProviderProps): React.ReactElement {
	const { instanceId, getAccessToken } = useAuth();

	// Track previous values to avoid reconfiguring on every render
	const configuredRef = useRef<{
		instanceId?: string;
		getAccessToken?: (forceRefresh?: boolean) => Promise<string | null>;
		baseUrl?: string;
		graphqlBaseUrl?: string;
	}>({});

	// Configure synchronously during render (not in useEffect) to avoid race condition
	// where child useQuery hooks fire before the API client is configured.
	if (
		configuredRef.current.instanceId !== instanceId ||
		configuredRef.current.getAccessToken !== getAccessToken ||
		configuredRef.current.baseUrl !== apiUrl ||
		configuredRef.current.graphqlBaseUrl !== apiGraphqlUrl
	) {
		configureApiClient({
			instanceId,
			getAccessToken,
			baseUrl: apiUrl,
			graphqlBaseUrl: apiGraphqlUrl,
		});
		configuredRef.current = {
			instanceId,
			getAccessToken,
			baseUrl: apiUrl,
			graphqlBaseUrl: apiGraphqlUrl,
		};
	}

	// Always render children — in demo mode there is no apiUrl, and that is expected.
	// The API client simply won't make live calls if unconfigured.
	return <>{children}</>;
}
